import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  GAME_SEAT_KEY,
  gameDocName,
  isGuestId,
  type GameMeta,
  type GameGuestSeat,
} from '@awwyeah/shared';
import { useYDoc, useYMap } from '../../sync';
import { BattleshipGame } from './BattleshipGame';
import { CheckersBoardPage } from './CheckersBoardPage';
import { PoolBoardPage } from './pool/PoolBoardPage';
import { BeerPongBoardPage } from './beerpong/BeerPongBoardPage';
import { Connect4Game } from './Connect4Game';
import { MancalaGame } from './mancala/MancalaGame';
import { GameTable } from './components/GameTable';
import { GuestSeatPanel } from './components/GuestSeatPanel';
import { useCurrentPlayer } from './guestPlayer';
import { mirrorGuestSeatToMeta, parseGuestSeat } from './guestSeat';
import { useGamesRegistry } from './useGamesRegistry';

/**
 * The board route (`/apps/game/:gameId`), reduced in WS-17c to what it always really was:
 * a dispatcher on `GameMeta.kind`. Everything a board needs beyond the registry entry — the
 * move log, the derived state, replay frames, and for Battleship the per-user secret doc —
 * belongs to the kind, so each kind owns its whole component rather than sharing a
 * lowest-common-denominator board with `if (kind === …)` scattered through it. The pieces
 * that genuinely *are* shared (the header, the replay transport) live in `components/`.
 *
 * Adding a kind is: an engine + a `derive` in `@awwyeah/shared`, a component here, one case
 * below. Nothing else in the app needs to know — the archive, replay, push and sharing
 * surfaces are all kind-generic already.
 *
 * WS-23 splits this into `GameBoard` (the dispatcher, reusable by the public guest page) and
 * the route wrapper below it, which is the half that needs the registry and a session.
 */
export function GameBoard({ gameId, meta }: { gameId: string; meta: GameMeta }): JSX.Element {
  const player = useCurrentPlayer();
  if (!player) return <></>;
  return (
    <GameTable meta={meta} player={player}>
      <KindBoard gameId={gameId} meta={meta} userId={player.id} />
    </GameTable>
  );
}

function KindBoard({
  gameId,
  meta,
  userId,
}: {
  gameId: string;
  meta: GameMeta;
  userId: string;
}): JSX.Element {
  if (meta.kind === 'battleship') return <BattleshipGame meta={meta} userId={userId} />;
  if (meta.kind === 'checkers') return <CheckersBoardPage gameId={gameId} meta={meta} />;
  if (meta.kind === 'pool') return <PoolBoardPage meta={meta} />;
  if (meta.kind === 'beerpong') return <BeerPongBoardPage meta={meta} />;
  if (meta.kind === 'mancala') return <MancalaGame meta={meta} userId={userId} />;
  return <Connect4Game meta={meta} userId={userId} />;
}

/**
 * Mirrors a guest's chosen name from the match doc up into the registry (WS-23).
 *
 * Only a member runs it, because only a member may write the `games` doc — and it is
 * deliberately cosmetic: gameplay never depends on it (both boards already agree on the seat
 * via `playerIds`, which the creator wrote at mint time). What it buys is that the games
 * index, the archive and the household timeline — none of which open a match doc — can say
 * "Dan vs Sam" instead of "Dan vs Guest".
 *
 * The unconditional `useGamesRegistry()` below is a read, not a write, so it stays
 * unconditional even though only a member runs the write effect: this hook only ever mounts
 * under `GameBoardWithSeat`, which only mounts under the auth-gated `/apps/game/:gameId`
 * route (`GameBoardPage`, below) — a guest, who has no household session, can never reach it.
 * A guest instead lands on `/play/:token` (`GuestPlayPage.tsx`), which renders `GameBoard`
 * directly and never mounts this hook at all.
 */
function useMirrorGuestSeat(meta: GameMeta): GameGuestSeat | null {
  const registries = useGamesRegistry();
  const player = useCurrentPlayer();
  const doc = useYDoc(gameDocName(meta.id));
  const { entries } = useYMap<unknown>(doc, GAME_SEAT_KEY);
  // Memoised so the effect below keys off the seat actually changing, not off a fresh object
  // being built on every render of a board that repaints on every move.
  const seat = useMemo(() => parseGuestSeat(Object.fromEntries(entries)), [entries]);

  const isMember = player !== null && !player.isGuest;
  useEffect(() => {
    if (!isMember || !seat) return;
    const next = mirrorGuestSeatToMeta(meta, seat);
    if (next) registries.map.set(meta.id, next);
  }, [isMember, seat, meta, registries.map]);

  return seat;
}

export function GameBoardPage(): JSX.Element {
  const { gameId } = useParams<{ gameId: string }>();
  const player = useCurrentPlayer();
  const registries = useGamesRegistry();

  const meta = registries.games.find((g) => g.id === gameId);

  if (!player || !gameId) return <></>;

  if (!meta) {
    return (
      <div className="flex flex-col gap-3 py-8 text-center" data-testid="game-not-found">
        <p className="text-sm text-fg-3">This game isn&apos;t here (yet, or ever).</p>
      </div>
    );
  }

  return <GameBoardWithSeat gameId={gameId} meta={meta} />;
}

/** Split out so the seat hooks run only once we know there is a game to run them for. */
function GameBoardWithSeat({ gameId, meta }: { gameId: string; meta: GameMeta }): JSX.Element {
  const seat = useMirrorGuestSeat(meta);
  const pendingGuestId = meta.playerIds.find((id) => isGuestId(id) && !meta.guestNames?.[id]);

  return (
    <div className="flex flex-col gap-3">
      {pendingGuestId && !seat && <GuestSeatPanel gameId={gameId} />}
      <GameBoard gameId={gameId} meta={meta} />
    </div>
  );
}
